"use client";

import { motion } from "motion/react";
import { useState } from "react";

import { readForm, submitAccessRequest } from "@/lib/access-request";
import {
  Field,
  FormError,
  Honeypot,
  PreferEmail,
  SubmitButton,
  SuccessState,
  inputClass,
} from "@/components/FormFields";

export function CreatorInviteForm() {
  const [submitted, setSubmitted] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fields = readForm(e.currentTarget);
    if (![fields.instagramHandle, fields.tiktokHandle, fields.youtubeHandle].some((h) => h?.trim())) {
      setError("Please add at least one social handle.");
      return;
    }
    setPending(true);
    setError(null);
    try {
      await submitAccessRequest("creator", fields);
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setPending(false);
    }
  }

  if (submitted) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}>
        <SuccessState
          title="Thank you! You're on the invite list."
          message="We'll reach out when applications open."
        />
      </motion.div>
    );
  }

  return (
    <>
      <h2 className="text-3xl font-display font-semibold text-gray-900 mb-8">
        Your Details
      </h2>

      <form onSubmit={handleSubmit} className="relative space-y-5">
        <Honeypot />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="First name" htmlFor="cr-first" required>
            <input id="cr-first" name="firstName" required type="text" autoComplete="given-name" placeholder="First name" className={inputClass} />
          </Field>
          <Field label="Last name" htmlFor="cr-last" required>
            <input id="cr-last" name="lastName" required type="text" autoComplete="family-name" placeholder="Last name" className={inputClass} />
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Email" htmlFor="cr-email" required>
            <input id="cr-email" name="email" required type="email" autoComplete="email" placeholder="you@email.com" className={inputClass} />
          </Field>
          <Field label="Country" htmlFor="cr-country" required>
            <input id="cr-country" name="country" required type="text" autoComplete="country-name" placeholder="Country" className={inputClass} />
          </Field>
        </div>

        <div className="space-y-4">
          <Field label="Instagram handle" htmlFor="cr-ig">
            <input id="cr-ig" name="instagramHandle" type="text" autoCapitalize="none" placeholder="@yourhandle" className={inputClass} />
          </Field>
          <Field label="TikTok handle" htmlFor="cr-tt">
            <input id="cr-tt" name="tiktokHandle" type="text" autoCapitalize="none" placeholder="@yourhandle" className={inputClass} />
          </Field>
          <Field label="YouTube Shorts channel/handle" htmlFor="cr-yt">
            <input id="cr-yt" name="youtubeHandle" type="text" autoCapitalize="none" placeholder="@yourchannel" className={inputClass} />
          </Field>
          <p className="text-xs text-gray-500">
            If you have multiple accounts, please list your largest channel on
            each platform.
          </p>
        </div>

        <FormError message={error} />

        <SubmitButton pending={pending}>Join the Invite List</SubmitButton>

        <PreferEmail />
      </form>
    </>
  );
}
